import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import styles from './lucezarepersonal_consultoria.module.scss';
import WhatsappIcon from '../../icons/whatsappIcon.tsx';

const WHATSAPP_NUMBER = '5517991852623';
const WHATSAPP_MSG =
    'Olá, Luciana! Vi a página de consultoria pelo link na bio e quero entender como funciona o acompanhamento e os planos. Podemos conversar?';

const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MSG)}`;

/** Ordem natural 1–10 (evita sort alfabético 1, 10, 2…) */
const GALLERY_ITEMS: { src: string; label: string }[] = [
    { src: require('./src/consultoria/consultoria_1.png'), label: '01' },
];

type Plan = {
    name: string;
    duration: string;
    badge?: string;
    description: string;
    items: string[];
    price: string;
};

const PLANS: Plan[] = [
    {
        name: 'Start Fit',
        duration: '1 mês',
        description: 'Ideal para começar com direção, organização e constância.',
        items: ['Treino personalizado', 'Avaliação física', 'Suporte via WhatsApp'],
        price: 'R$ 99 por mês',
    },
    {
        name: 'Shape Progressivo',
        duration: '60 dias',
        badge: 'Queridinho',
        description: 'Perfeito para quem quer evolução consistente com mais acompanhamento.',
        items: [
            'Treino personalizado',
            'Suporte no WhatsApp de seg. a sex.',
            'Avaliação física',
            'Periodização',
            'Orientação e dicas de alimentação',
            '1 troca de treino personalizado',
        ],
        price: 'R$ 150 a cada 60 dias ou R$ 75 mensais',
    },
    {
        name: 'Transformação 90D',
        duration: 'Trimestral',
        badge: 'Projeto Evolução',
        description: 'Para quem busca resultado mais visível com estratégia mais completa.',
        items: [
            'Treino com progressão',
            'Suporte prioritário no WhatsApp',
            'Avaliação física inicial e final',
            '2 trocas de treino, a cada 45 dias',
            'Orientação de cardio e estratégias alimentares',
        ],
        price: 'R$ 270 o pacote ou R$ 90 mensais',
    },
    {
        name: 'Projeto Shape 180',
        duration: 'Semestral',
        description: 'Um plano robusto para consolidar shape, rotina e performance.',
        items: [
            'Periodização completa',
            'Suporte via WhatsApp',
            'Avaliação física inicial e final',
            'Métodos de treino',
            '3 trocas de treino personalizadas, a cada 60 dias',
            'Orientação alimentar e estratégias de treino/cardio',
        ],
        price: 'R$ 499 o pacote ou R$ 85 mensais',
    },
    {
        name: 'Projeto Alta Performance 365',
        duration: 'Anual',
        badge: 'Método Corpo de Elite',
        description: 'Acompanhamento premium para quem quer máxima evolução ao longo do ano.',
        items: [
            'Acompanhamento completo',
            'Periodização',
            'Treinos avançados',
            'Técnicas com estratégias exclusivas de treino',
            'Suporte a protocolos',
            'Ajustes em tempo real',
            'Suporte VIP no WhatsApp',
            'Planejamento anual de shape e saúde',
            'Estratégias de treino, cardio e dicas alimentares',
            '6 treinos personalizados, a cada 60 dias',
        ],
        price: 'R$ 950 o pacote ou R$ 80 mensais',
    },
];

function getPlanWhatsappLink(plan: Plan): string {
    const message = `Olá, Luciana! Vim pela página de consultoria e tenho interesse no plano ${plan.name} (${plan.duration}). Pode me passar mais detalhes?`;
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

const Lucezarepersonal_consultoria: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className={styles.lucezarepersonal_consultoria}>
            <div className={styles.page}>
                <div className={styles.header}>
                    <Link
                        to="/lucezarepersonal"
                        state={{ skipLoader: true }}
                        className={styles.backButton}
                        aria-label="Voltar ao link na bio"
                    >
                        <FaArrowLeft />
                    </Link>
                    <div className={styles.headerText}>
                        <p className={styles.eyebrow}>Luciana Cezare</p>
                        <h1 className={styles.title}>Consultoria online</h1>
                    </div>
                </div>

                <p className={styles.intro}>
                    Abaixo você confere o material completo: como funciona o método, diferenciais do acompanhamento e
                    informações para decidir com segurança.
                </p>

                <div className={styles.grid}>
                    {GALLERY_ITEMS.map((item, index) => (
                        <figure key={item.src} className={styles.card}>
                            <img
                                src={item.src}
                                alt={`Consultoria Luciana Cezare - slide ${item.label}`}
                                className={styles.cardImage}
                                loading={index < 2 ? 'eager' : 'lazy'}
                            />
                            <figcaption className={styles.cardFigcaption}>{item.label}</figcaption>
                        </figure>
                    ))}
                </div>

                <section className={styles.plansSection} aria-labelledby="planos-luciana-heading">
                    <div className={styles.plansHeader}>
                        <p className={styles.plansEyebrow}>Planos</p>
                        <h2 id="planos-luciana-heading" className={styles.plansTitle}>
                            Escolha o acompanhamento ideal para o seu momento
                        </h2>
                        <p className={styles.plansIntro}>
                            Cada plano foi pensado para um nível diferente de acompanhamento. Quanto mais completo,
                            maior o suporte, as estratégias e a personalização para acelerar seus resultados.
                        </p>
                    </div>

                    <div className={styles.plansGrid}>
                        {PLANS.map((plan) => (
                            <article key={plan.name} className={styles.planCard}>
                                <div className={styles.planTop}>
                                    <div>
                                        <p className={styles.planDuration}>{plan.duration}</p>
                                        <h3 className={styles.planName}>{plan.name}</h3>
                                    </div>
                                    {plan.badge && <span className={styles.planBadge}>{plan.badge}</span>}
                                </div>

                                <p className={styles.planDescription}>{plan.description}</p>

                                <ul className={styles.planList}>
                                    {plan.items.map((item) => (
                                        <li key={item}>{item}</li>
                                    ))}
                                </ul>

                                <p className={styles.planPrice}>{plan.price}</p>

                                <a
                                    href={getPlanWhatsappLink(plan)}
                                    className={styles.planButton}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <WhatsappIcon height="22" color="#ffffff" />
                                    <span>Quero este plano</span>
                                </a>
                            </article>
                        ))}
                    </div>
                </section>

                <div className={styles.ctaSection} aria-labelledby="cta-consultoria-luciana-heading">
                    <h2 id="cta-consultoria-luciana-heading" className={styles.ctaHeading}>
                        Vamos conversar sobre seus objetivos e o melhor plano para você
                    </h2>
                    <p className={styles.ctaText}>
                        Se você se identificou com o que viu nas imagens, o próximo passo é simples: falar comigo no
                        WhatsApp. Tiramos dúvidas sobre formatos de consultoria, acompanhamento e como começar sem
                        complicação, com a mesma proximidade de quem acompanha alunos todos os dias.
                    </p>
                    <a href={whatsappLink} className={styles.ctaWhatsapp} target="_blank" rel="noopener noreferrer">
                        <WhatsappIcon height="28" color="#ffffff" />
                        <span>Quero saber mais</span>
                    </a>
                </div>

                <p className={styles.footerNote}>
                    Informações comerciais e condições podem ser confirmadas diretamente no atendimento. Resultados
                    dependem de adesão individual ao programa.
                </p>
            </div>
        </div>
    );
};

export default Lucezarepersonal_consultoria;
